import React, { useMemo } from 'react';

import config from 'config';
import cloneDeep from 'lodash/cloneDeep';
import { defineMessages } from 'react-intl';

import { ProductTag } from '~/shared/constants/products';
import {
  LabelType,
  useProductBrowser,
  slugify,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import {
  FormattedMessage,
  useIntl,
  useRegion,
} from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import useCustomerIsGendered from './useCustomerIsGendered';

const useBreadcrumbsForSizeFilters = config.get(
  'public.brand.features.useBreadcrumbsForSizeFilters'
);

const DynamicFormattedMessage = FormattedMessage;

const categoryLabels = defineMessages({
  all: {
    id: 'site_product_category.all',
    defaultMessage: 'All',
  },
});

function getCategoryMessage(category, labelOptions) {
  const { isTopLevel, labelType } = labelOptions;

  if (isTopLevel) {
    switch (labelType) {
      case LabelType.CATEGORY_DROPDOWN:
        return {
          id: `site_product_category.all_${slugify(category.label)}`,
          defaultMessage: `All ${category.label}`,
        };
      case LabelType.CATEGORY_TREE:
        return categoryLabels.all;
    }
  }
  // Return nothing to use default.
}

const getSizeFilterDeterminer = (
  useBreadcrumbsForSizeFilters,
  isMensPage,
  gender
) => {
  if (useBreadcrumbsForSizeFilters) {
    return isMensPage ? 'M' : 'F';
  }

  return gender;
};

const sizeByDomain = ({ tld }, sizeMap) => {
  switch (tld) {
    case '.co.uk':
      return sizeMap.EU;
    case '.com':
    case '.ca':
      return sizeMap.US;
    default:
      return sizeMap.FR_ES;
  }
};

export function useCategoryLabels({ useDefaultLabels }) {
  const defaultLabels = useDefaultLabels();
  const intl = useIntl();

  return {
    renderLabel: (category, labelOptions = {}) => {
      const message = getCategoryMessage(category, labelOptions);
      if (message) {
        return <DynamicFormattedMessage {...message} />;
      }
      return defaultLabels.renderLabel(category, labelOptions);
    },
    renderLabelString: (category, labelOptions = {}) => {
      const message = getCategoryMessage(category, labelOptions);
      if (message) {
        return intl.formatMessage(message);
      }
      return defaultLabels.renderLabel(category, labelOptions);
    },
  };
}

export function useOptionLabels({ useDefaultLabels }) {
  const defaultLabels = useDefaultLabels();
  const { tld } = useDomain();
  const productBrowser = useProductBrowser();
  const region = useRegion();
  const gender = useCustomerIsGendered();
  const backgroundCategoryIds = useMemo(
    () => productBrowser?.filters.backgroundCategoryIds || [],
    [productBrowser]
  );

  const mensId = config.get('public.productCategories.mens');
  const mensTopId = config.get('public.productCategories.mensTop');
  const mensBottomId = config.get('public.productCategories.mensBottom');
  const sizeDomainMaps = config.get('public.brand.sizeDomainMaps');
  const shoeSizeMap = useMemo(
    () => sizeByDomain({ tld }, sizeDomainMaps.unisex['shoe-size']),
    [sizeDomainMaps.unisex, tld]
  );
  const kidsSizeMap = config.get('public.brand.kidsSizeMap');

  const sizeLabels = useMemo(() => {
    // checking if page is all mens
    const isMensAll = backgroundCategoryIds.includes(mensId);

    const isMensBottom =
      backgroundCategoryIds.includes(mensBottomId) || isMensAll;

    const isMensTop = backgroundCategoryIds.includes(mensTopId) || isMensAll;

    let mensPositionKey = null;
    if (isMensTop) {
      mensPositionKey = 'Top';
    } else if (isMensBottom) {
      mensPositionKey = 'Bottom';
    }

    const sizeFilterDeterminer = getSizeFilterDeterminer(
      useBreadcrumbsForSizeFilters,
      isMensBottom || isMensTop || isMensAll,
      gender
    );

    let sizeFilterOptions = config.get('public.brand.sizeFilterOptions')[
      region
    ][tld][sizeFilterDeterminer];

    const sizeFilterLabels = {};

    let maleBottomSizeFilterOptions = config.get(
      'public.brand.sizeFilterOptions'
    )[region][sizeFilterDeterminer];

    const maleBottomSizeFilterLabels = {};

    if (mensPositionKey) {
      if (isMensBottom) {
        maleBottomSizeFilterOptions = maleBottomSizeFilterOptions.Bottom;
        Object.keys(maleBottomSizeFilterOptions).forEach(
          (key) =>
            (maleBottomSizeFilterLabels[key] =
              maleBottomSizeFilterOptions[key].label)
        );
        return maleBottomSizeFilterLabels;
      }
      sizeFilterOptions = sizeFilterOptions[mensPositionKey];
      Object.keys(sizeFilterOptions).forEach(
        (key) => (sizeFilterLabels[key] = sizeFilterOptions[key].label)
      );
      return sizeFilterLabels;
    }

    Object.keys(sizeFilterOptions).forEach(
      (key) => (sizeFilterLabels[key] = sizeFilterOptions[key].label)
    );

    return sizeFilterLabels;
  }, [
    backgroundCategoryIds,
    gender,
    mensBottomId,
    mensId,
    mensTopId,
    region,
    tld,
  ]);

  const sizeFieldRegex = /^size(_|$)/;

  return {
    renderLabel: (filterField, option, labelOptions = {}) => {
      if (filterField.field === 'size_kid') {
        return kidsSizeMap[option.value].label || option.value;
      }
      if (filterField.field === 'size_shoes') {
        return shoeSizeMap[option.value] || option.value;
      }
      if (sizeFieldRegex.test(filterField.field)) {
        return sizeLabels[option.value] || option.value;
      }

      return defaultLabels.renderLabel(filterField, option, labelOptions);
    },
    renderLabelString: (filterField, option, labelOptions = {}) => {
      if (sizeFieldRegex.test(filterField.field)) {
        return sizeLabels[option.value] || option.value;
      }
      return defaultLabels.renderLabelString(filterField, option, labelOptions);
    },
  };
}

const swatchTagLabels = defineMessages({
  khloesPick: {
    id: 'site_product_color.swatch_tag_khloes_pick',
    defaultMessage: 'Khloe&rsquo;s Pick',
  },
});

function renderSwatchTagLabel({ product, option }) {
  const { relatedProductIdObjectList } = product;
  const relatedProduct = relatedProductIdObjectList?.find(
    (product) => product.relatedProductId === option.masterProductId
  );
  if (!relatedProduct) {
    return null;
  }
  if (relatedProduct.tagIdList.includes(ProductTag.khloesPick)) {
    return <DynamicFormattedMessage {...swatchTagLabels.khloesPick} />;
  }
}

export function usePDPOptionLabels({ useDefaultLabels }) {
  const defaultLabels = useDefaultLabels();
  const { product } = useProductDetailContext();
  const sizeFieldRegex = /^size(_|$)/;

  return {
    renderLabel: (filterField, option, labelOptions = {}, isSummary) => {
      if (sizeFieldRegex.test(filterField.field)) {
        return option.value;
      }

      // for color swatch tag labels, we need to check if we are swatches
      // and if we are not in the summary render
      if (filterField.field === 'Color' && !isSummary) {
        return renderSwatchTagLabel({ product, option });
      }

      return defaultLabels.renderLabel(filterField, option, labelOptions);
    },
    renderLabelString: (filterField, option, labelOptions = {}) => {
      if (sizeFieldRegex.test(filterField.field)) {
        return option.value;
      }
      return defaultLabels.renderLabelString(filterField, option, labelOptions);
    },
  };
}

export function getLabelSortedInitialAggregations(
  aggregations = [],
  region,
  tld,
  gender
) {
  let sizeFilterOptions = config.get('public.brand.sizeFilterOptions')[region][
    tld
  ][gender];
  const kidsSizeMap = config.get('public.brand.kidsSizeMap');
  const allowedFilterValues = config.get('public.brand.allowedFilterValues');

  const sortedAggregations = cloneDeep(aggregations);
  return sortedAggregations?.map((aggregation) => {
    if (aggregation.field.startsWith('size')) {
      sizeFilterOptions =
        aggregation.field === 'size_kid' ? kidsSizeMap : sizeFilterOptions;
      aggregation.items = aggregation.items.map((item) => {
        if (sizeFilterOptions[item.value]?.sort) {
          item.sort = sizeFilterOptions[item.value].sort;
        }
        return item;
      });

      // filter down to just the superset of options that should be allowed per aggregation if applicable
      // as well as remove any without a sort
      aggregation.items = aggregation.items.filter(
        (item) =>
          (!allowedFilterValues[aggregation.field] ||
            allowedFilterValues[aggregation.field].includes(item.value)) &&
          item.sort !== undefined
      );
      aggregation.items.sort(
        ({ sort: a }, { sort: b }) => (a ?? 99) - (b ?? 99)
      );
    }
    return aggregation;
  });
}
